/**
 * Body-metrics maths for the `bodyMetrics` question: BMI from the weight and
 * height the nurse enters, plus the clinical band that number falls into.
 *
 * The band is AGE-AND-SEX AWARE. A child's BMI is meaningless against the adult
 * WHO cutoffs — a 5-year-old girl at BMI 14.9 sits near the 45th percentile and
 * is an ordinary healthy child, but the adult table calls anything under 18.5
 * "Underweight". Ages 2 through 19 are therefore classified against the CDC
 * BMI-for-age percentiles (`cdc-bmi-lms.ts`), which is the standard of care;
 * 20 and over keep the WHO adult cutoffs. Under 2 gets NO category at all,
 * because BMI-for-age is not defined there — infants are assessed on
 * weight-for-length, a different chart we do not collect the inputs for.
 *
 * Kept free of React so the arithmetic and the cutoffs can be tested directly,
 * the same way `bp-classify.ts` separates classification from the BP component.
 */

import { CDC_AGE_BANDS, CDC_BMI_LMS_FEMALE, CDC_BMI_LMS_MALE, type LmsRow } from "./cdc-bmi-lms";

export type BmiCategory =
    | "underweight"
    | "normal"
    | "overweight"
    | "obese_1"
    | "obese_2"
    | "obese_3"
    | "obesity"
    | "severe_obesity";

export const BMI_CATEGORY_LABEL: Record<BmiCategory, string> = {
    underweight: "Underweight",
    normal: "Within normal range",
    overweight: "Overweight",
    obese_1: "Obesity, Class I",
    obese_2: "Obesity, Class II",
    obese_3: "Severe obesity, Class III",
    // CDC pediatric bands. Deliberately NOT reusing obese_1/2/3: the child
    // bands are percentile-derived and do not correspond to the WHO classes,
    // so a reader of the chart or the PDF must not be able to confuse them.
    obesity: "Obesity",
    severe_obesity: "Severe obesity"
};

/** The CDC pediatric bands, so a caller can tell which scale produced a value. */
export const PEDIATRIC_CATEGORIES: ReadonlySet<BmiCategory> = new Set<BmiCategory>([
    "obesity",
    "severe_obesity"
]);

export interface MetricRange {
    min: number;
    max: number;
    warningMin: number;
    warningMax: number;
    unit: string;
}

/**
 * Adult bounds. The weight numbers are carried over verbatim from
 * `RANGE_RULES["87"]` in `NumberQuestion.tsx` — the guard the old "Current
 * weight (lbs):" question showed. That map is keyed by question id and only
 * consulted by `NumberQuestion`, so the bounds do not follow the field into a
 * new component on their own; they have to be restated here or nurses quietly
 * lose them.
 *
 * Height has no precedent in the form — nothing has ever collected it — so the
 * band covers every adult a home-care nurse realistically assesses, including
 * wheelchair-bound and unusually short or tall patients.
 */
export const ADULT_METRIC_RANGES: { weight: MetricRange; height: MetricRange } = {
    weight: { min: 50, max: 500, warningMin: 80, warningMax: 400, unit: "lbs" },
    height: { min: 36, max: 84, warningMin: 48, warningMax: 78, unit: "in" }
};

/**
 * Kept as the previous export name so existing call sites and tests that only
 * ever meant "the adult bands" keep working.
 * @deprecated Use `metricRangesForAge(ageYears)` — the adult numbers hard-block
 * nothing now, but they still produce a nonsense warning on a pediatric visit.
 */
export const BODY_METRIC_RANGES = ADULT_METRIC_RANGES;

/**
 * The bands to judge one patient's entries against.
 *
 * Ages 0-19 come off the CDC 3rd/97th-percentile table; 20 and over get the
 * adult numbers. The `min`/`max` tier is set well outside the percentile band
 * because at that distance the value is almost certainly a mis-key rather than
 * an unusual patient, and that is the only distinction the two tiers draw —
 * neither tier blocks anything.
 *
 * An unknown age falls back to the WIDEST reading of both: the form must not
 * warn about a plausible child merely because the patient record is missing a
 * date of birth.
 */
export function metricRangesForAge(ageYears: number | null | undefined): {
    weight: MetricRange;
    height: MetricRange;
} {
    if (ageYears == null || !isFinite(ageYears)) {
        const infant = CDC_AGE_BANDS[0];
        return {
            weight: {
                min: round1(infant[1] * 0.4),
                max: ADULT_METRIC_RANGES.weight.max,
                warningMin: round1(infant[1]),
                warningMax: ADULT_METRIC_RANGES.weight.warningMax,
                unit: "lbs"
            },
            height: {
                min: round1(infant[3] * 0.7),
                max: ADULT_METRIC_RANGES.height.max,
                warningMin: round1(infant[3]),
                warningMax: ADULT_METRIC_RANGES.height.warningMax,
                unit: "in"
            }
        };
    }
    if (ageYears >= 20) {
        return ADULT_METRIC_RANGES;
    }
    const band = CDC_AGE_BANDS[Math.max(0, Math.min(19, Math.floor(ageYears)))];
    const [, weightLow, weightHigh, heightLow, heightHigh] = band;
    return {
        weight: {
            min: round1(weightLow * 0.6),
            max: round1(weightHigh * 1.8),
            warningMin: round1(weightLow),
            warningMax: round1(weightHigh),
            unit: "lbs"
        },
        height: {
            min: round1(heightLow * 0.75),
            max: round1(heightHigh * 1.25),
            warningMin: round1(heightLow),
            warningMax: round1(heightHigh),
            unit: "in"
        }
    };
}

function round1(n: number): number {
    return Math.round(n * 10) / 10;
}

/** Total inches from a feet + inches entry, or null if feet are missing/non-numeric. */
export function heightToInches(feetStr: string, inchesStr: string): number | null {
    if (!feetStr) {
        return null;
    }
    const feet = parseFloat(feetStr);
    if (isNaN(feet)) {
        return null;
    }
    const inches = inchesStr ? parseFloat(inchesStr) : 0;
    if (isNaN(inches)) {
        return null;
    }
    return feet * 12 + inches;
}

/**
 * BMI from pounds and total inches, to one decimal place.
 * Returns null for an incomplete or unusable reading rather than NaN/Infinity,
 * so callers can treat "no BMI yet" as one case.
 */
export function computeBmi(weightStr: string, heightInches: number | null): number | null {
    if (!weightStr || heightInches === null || heightInches <= 0) {
        return null;
    }
    const weight = parseFloat(weightStr);
    if (isNaN(weight) || weight <= 0) {
        return null;
    }
    return Math.round(((703 * weight) / (heightInches * heightInches)) * 10) / 10;
}

/** WHO adult cutoffs. Each boundary belongs to the higher band. */
export function classifyBmi(bmi: number | null): BmiCategory | null {
    if (bmi === null || isNaN(bmi)) {
        return null;
    }
    if (bmi < 18.5) {
        return "underweight";
    }
    if (bmi < 25) {
        return "normal";
    }
    if (bmi < 30) {
        return "overweight";
    }
    if (bmi < 35) {
        return "obese_1";
    }
    if (bmi < 40) {
        return "obese_2";
    }
    return "obese_3";
}

// ── CDC BMI-for-age, ages 2 through 19 ──

/**
 * Abramowitz & Stegun 26.2.17 — the standard normal CDF to ~7 decimal places.
 * Hand-rolled because React Native's JS runtime has no `erf` and pulling a
 * stats package in for one function is not worth the bundle.
 */
function normalCdf(z: number): number {
    const sign = z < 0 ? -1 : 1;
    const x = Math.abs(z) / Math.SQRT2;
    const t = 1 / (1 + 0.3275911 * x);
    const y =
        1 -
        ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
            0.254829592) *
            t *
            Math.exp(-x * x);
    return 0.5 * (1 + sign * y);
}

/** Linear interpolation of the LMS triple at an arbitrary age in months. */
function lmsAt(table: readonly LmsRow[], ageMonths: number): [number, number, number] | null {
    const first = table[0];
    const last = table[table.length - 1];
    if (ageMonths < first[0] || ageMonths > last[0]) {
        return null;
    }
    let lo = 0;
    let hi = table.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (table[mid][0] <= ageMonths) {
            lo = mid;
        } else {
            hi = mid;
        }
    }
    const a = table[lo];
    const b = table[hi];
    const span = b[0] - a[0];
    const t = span === 0 ? 0 : (ageMonths - a[0]) / span;
    return [a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t];
}

/**
 * Where this BMI sits on the CDC BMI-for-age chart, 0-100.
 *
 * Needs a sex, because the male and female charts diverge sharply through
 * puberty; a missing or unrecognised gender returns null rather than silently
 * picking one. Returns null outside 24-240 months for the same reason.
 */
export function bmiPercentileForAge(
    bmi: number | null,
    ageMonths: number | null | undefined,
    gender: string | null | undefined
): number | null {
    if (bmi === null || isNaN(bmi) || bmi <= 0 || ageMonths == null || !isFinite(ageMonths)) {
        return null;
    }
    const normalized = (gender ?? "").trim().toLowerCase();
    const table =
        normalized === "male" || normalized === "m"
            ? CDC_BMI_LMS_MALE
            : normalized === "female" || normalized === "f"
              ? CDC_BMI_LMS_FEMALE
              : null;
    if (!table) {
        return null;
    }
    const lms = lmsAt(table, ageMonths);
    if (!lms) {
        return null;
    }
    const [L, M, S] = lms;
    const z = L === 0 ? Math.log(bmi / M) / S : (Math.pow(bmi / M, L) - 1) / (L * S);
    if (!isFinite(z)) {
        return null;
    }
    return Math.round(normalCdf(z) * 1000) / 10;
}

/**
 * CDC pediatric weight-status bands, from the BMI-for-age percentile:
 * under 5th underweight, 5th-85th healthy, 85th-95th overweight, 95th and up
 * obesity, and 120% of the 95th-percentile BMI and up severe obesity.
 */
function classifyPediatric(
    bmi: number,
    percentile: number,
    ageMonths: number,
    table: readonly LmsRow[]
): BmiCategory {
    if (percentile >= 95) {
        const lms = lmsAt(table, ageMonths);
        if (lms) {
            const [L, M, S] = lms;
            // BMI at the 95th percentile for this age/sex, inverted from z.
            const z95 = 1.6448536269514722;
            const bmi95 = L === 0 ? M * Math.exp(S * z95) : M * Math.pow(1 + L * S * z95, 1 / L);
            if (isFinite(bmi95) && bmi >= bmi95 * 1.2) {
                return "severe_obesity";
            }
        }
        return "obesity";
    }
    if (percentile >= 85) {
        return "overweight";
    }
    if (percentile < 5) {
        return "underweight";
    }
    return "normal";
}

export interface BmiAssessment {
    /** Null when no scale applies — an infant, or a child with no sex on file. */
    category: BmiCategory | null;
    /** CDC BMI-for-age percentile, children only. Null for adults and infants. */
    percentile: number | null;
    /** Which reference produced the result, for the label the nurse reads. */
    scale: "adult" | "pediatric" | "none";
    /** Why there is no category, when there isn't one. */
    reason: string | null;
}

/**
 * The one entry point the UI and the answer serializer should call.
 *
 * Picks the reference that actually applies to this patient rather than
 * assuming every patient is an adult, which is what produced a false
 * "Underweight" finding on healthy children.
 */
export function assessBmi(input: {
    bmi: number | null;
    ageMonths: number | null;
    gender: string | null;
}): BmiAssessment {
    const { bmi, ageMonths, gender } = input;
    if (bmi === null || isNaN(bmi)) {
        return { category: null, percentile: null, scale: "none", reason: null };
    }
    // No age on file: the adult chart is the only defensible default for a
    // home-care census that is overwhelmingly adult, and it is what the
    // question did before.
    if (ageMonths == null || !isFinite(ageMonths)) {
        return {
            category: classifyBmi(bmi),
            percentile: null,
            scale: "adult",
            reason: null
        };
    }
    if (ageMonths >= 240) {
        return { category: classifyBmi(bmi), percentile: null, scale: "adult", reason: null };
    }
    if (ageMonths < 24) {
        return {
            category: null,
            percentile: null,
            scale: "none",
            reason: "BMI is not classified under age 2 — infants are assessed on weight-for-length."
        };
    }
    const percentile = bmiPercentileForAge(bmi, ageMonths, gender);
    if (percentile === null) {
        return {
            category: null,
            percentile: null,
            scale: "none",
            reason: "BMI percentile needs the patient's sex on file to classify a child."
        };
    }
    const normalized = (gender ?? "").trim().toLowerCase();
    const table =
        normalized === "male" || normalized === "m" ? CDC_BMI_LMS_MALE : CDC_BMI_LMS_FEMALE;
    return {
        category: classifyPediatric(bmi, percentile, ageMonths, table),
        percentile,
        scale: "pediatric",
        reason: null
    };
}

/**
 * A message for a value that is far enough off to look like a mis-key, or
 * merely outside the usual band for this patient's age, or null when it looks
 * ordinary.
 *
 * ADVISORY ONLY. This function's output has never gated anything for the
 * legacy weight question — `NumberQuestion` drew a red chip and let the nurse
 * carry on — and it must not gate anything here either. A real 43-lb
 * five-year-old is not a reason to refuse to submit a visit.
 */
export function checkMetricRange(value: string, range: MetricRange, label: string): string | null {
    if (!value) {
        return null;
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
        return null;
    }
    if (num < range.min || num > range.max) {
        return `${label} ${num} ${range.unit} is outside the expected range (${range.min}–${range.max}). Please verify.`;
    }
    if (num < range.warningMin) {
        return `${label} ${num} ${range.unit} is unusually low. Please confirm.`;
    }
    if (num > range.warningMax) {
        return `${label} ${num} ${range.unit} is unusually high. Please confirm.`;
    }
    return null;
}

/** Whole years between a `YYYY-MM-DD` dob and `now`, or null if unusable. */
export function ageFromDob(dob: string | null | undefined, now: Date): number | null {
    if (!dob) {
        return null;
    }
    const born = new Date(dob);
    if (isNaN(born.getTime())) {
        return null;
    }
    let age = now.getUTCFullYear() - born.getUTCFullYear();
    const monthDiff = now.getUTCMonth() - born.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < born.getUTCDate())) {
        age -= 1;
    }
    return age;
}

/**
 * Whole months between a `YYYY-MM-DD` dob and `now`, or null if unusable.
 *
 * Months, not years, because the CDC chart moves fast in early childhood —
 * the median BMI for a girl shifts by about 0.4 between ages 4 and 6, and
 * rounding a 71-month-old down to "5" throws the percentile off enough to move
 * a borderline child across a band.
 */
export function ageMonthsFromDob(dob: string | null | undefined, now: Date): number | null {
    if (!dob) {
        return null;
    }
    const born = new Date(dob);
    if (isNaN(born.getTime())) {
        return null;
    }
    let months =
        (now.getUTCFullYear() - born.getUTCFullYear()) * 12 +
        (now.getUTCMonth() - born.getUTCMonth());
    if (now.getUTCDate() < born.getUTCDate()) {
        months -= 1;
    }
    return months < 0 ? null : months;
}

export interface BodyMetricsAnswer {
    weightLbs: string;
    heightFt: string;
    heightIn: string;
    bmi: number | null;
    category: BmiCategory | null;
    ageYears: number | null;
    gender: string | null;
    /** Age in months at the time of the visit — what the CDC chart is read at. */
    ageMonths?: number | null;
    /** CDC BMI-for-age percentile. Present only for a classified 2-19 patient. */
    percentile?: number | null;
    /** Which reference the category came from, so a reader cannot mistake one for the other. */
    scale?: "adult" | "pediatric" | "none";
}

const EMPTY_ANSWER: BodyMetricsAnswer = {
    weightLbs: "",
    heightFt: "",
    heightIn: "",
    bmi: null,
    category: null,
    ageYears: null,
    gender: null,
    ageMonths: null,
    percentile: null,
    scale: "none"
};

/**
 * Serialize the card into the single string stored in `visit_answers.value`.
 *
 * BMI, the percentile and the category are DERIVED here and stored alongside
 * the inputs, on purpose: the PDF renderer, the LLM prompt builder and the
 * auto-compute rules all read this row, and none of them should have to
 * re-derive the number the nurse actually saw. Age and gender are snapshotted
 * for the same reason — the patient record can change after the visit is
 * signed, and a percentile read against a different age is a different
 * finding.
 */
export function buildBodyMetricsValue(input: {
    weightLbs: string;
    heightFt: string;
    heightIn: string;
    ageYears: number | null;
    ageMonths?: number | null;
    gender: string | null;
}): string {
    const bmi = computeBmi(input.weightLbs, heightToInches(input.heightFt, input.heightIn));
    const ageMonths =
        input.ageMonths ?? (input.ageYears != null ? Math.round(input.ageYears * 12) : null);
    const assessment = assessBmi({ bmi, ageMonths, gender: input.gender });
    const answer: BodyMetricsAnswer = {
        weightLbs: input.weightLbs,
        heightFt: input.heightFt,
        heightIn: input.heightIn,
        bmi,
        category: assessment.category,
        ageYears: input.ageYears,
        gender: input.gender,
        ageMonths,
        percentile: assessment.percentile,
        scale: assessment.scale
    };
    return JSON.stringify(answer);
}

/** Read the stored answer back, tolerating empty/garbage without throwing. */
export function parseBodyMetrics(value: string | undefined | null): BodyMetricsAnswer {
    if (!value) {
        return { ...EMPTY_ANSWER };
    }
    try {
        const parsed = JSON.parse(value) as Partial<BodyMetricsAnswer>;
        return { ...EMPTY_ANSWER, ...parsed };
    } catch {
        return { ...EMPTY_ANSWER };
    }
}

/**
 * Is this card actually filled in?
 *
 * `validateRequired` in the section screen only asks whether the stored string
 * is non-empty, and a JSON answer clears that bar as soon as the nurse types a
 * single digit — so the section could otherwise be completed with a weight, no
 * height and no BMI. This asks the one question that gate is entitled to ask:
 * are both measurements present and usable?
 *
 * It deliberately does NOT consult `checkMetricRange`. It used to, and that
 * turned an advisory warning into a hard completion blocker: a five-year-old
 * at 43 lbs fell under the adult 50-lb floor, so a truthfully-filled card was
 * reported to the nurse as "This field is required" with no way to proceed
 * short of entering a false weight. Plausibility is the nurse's judgement to
 * exercise, not the form's to enforce.
 */
export function isBodyMetricsComplete(value: string | undefined | null): boolean {
    const parsed = parseBodyMetrics(value);
    if (!parsed.weightLbs || !parsed.heightFt) {
        return false;
    }
    const weight = parseFloat(parsed.weightLbs);
    if (isNaN(weight) || weight <= 0) {
        return false;
    }
    const inches = heightToInches(parsed.heightFt, parsed.heightIn);
    if (inches === null || inches <= 0) {
        return false;
    }
    return true;
}
